import React, { useEffect, useState, lazy } from 'react';
interface ResponsiveImageProps {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  aspectRatio?: string;
  quality?: 'low' | 'medium' | 'high';
  loadingEffect?: 'blur' | 'fade' | 'none';
  priority?: boolean;
}
export const ResponsiveImage: React.FC<ResponsiveImageProps> = ({
  src,
  alt,
  className = '',
  sizes = '(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw',
  aspectRatio = '3/4',
  quality = 'medium',
  loadingEffect = 'blur',
  priority = false
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [error, setError] = useState(false);
  // Generate srcSet based on the original image URL
  const generateSrcSet = (url: string) => {
    // Function to add width parameter to URL
    const addWidthParam = (url: string, width: number) => {
      // This is a simplified example - in a real app you'd likely use a CDN or image service
      // that supports resizing via URL parameters
      return `${url}?w=${width} ${width}w`;
    };
    return [400, 600, 800, 1200, 1600].map(width => addWidthParam(url, width)).join(', ');
  };
  // Get appropriate quality placeholder based on setting
  const getPlaceholderUrl = (url: string) => {
    // In a real app, you'd generate proper placeholders
    // This is just a simplified example
    return `${url}?quality=10&blur=20`;
  };
  // Set up intersection observer to detect when image enters viewport
  useEffect(() => {
    if (!priority && typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      }, {
        rootMargin: '200px'
      });
      const element = document.getElementById(`img-${src.replace(/[^\w]/g, '')}`);
      if (element) {
        observer.observe(element);
      }
      return () => observer.disconnect();
    } else {
      // If priority is true or IntersectionObserver not available, load immediately
      setIsInView(true);
    }
  }, [src, priority]);
  // Get loading class based on effect type
  const getLoadingClass = () => {
    if (!isLoaded) {
      switch (loadingEffect) {
        case 'blur':
          return 'filter blur-sm scale-105';
        case 'fade':
          return 'opacity-50';
        default:
          return '';
      }
    }
    return '';
  };
  // Get transition class based on effect type
  const getTransitionClass = () => {
    switch (loadingEffect) {
      case 'blur':
        return 'transition-all duration-500';
      case 'fade':
        return 'transition-opacity duration-500';
      default:
        return '';
    }
  };
  return <div id={`img-${src.replace(/[^\w]/g, '')}`} className={`overflow-hidden ${className}`} style={{
    aspectRatio
  }} data-id="element-21653">
      {(isInView || priority) && <img src={error ? getPlaceholderUrl(src) : src} srcSet={error ? '' : generateSrcSet(src)} sizes={sizes} alt={alt} className={`w-full h-full object-cover ${getLoadingClass()} ${getTransitionClass()}`} onLoad={() => setIsLoaded(true)} onError={() => setError(true)} loading={priority ? 'eager' : 'lazy'} data-id="element-21654" />}
    </div>;
};